import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { Pagination } from '@/dsm-components';
import usePermissions from '@/hooks/usePermissions';
import { type AvlItem, useBulkUpdateAvlItemsMutation, useGetAvlItemsQuery } from '@/services/approvedVendorList';
import {
  type DataTableColumn,
  type DataTableSortState,
  PalmettoButton,
  PalmettoDataTable,
  PalmettoIcon,
  PalmettoSelect,
  PalmettoTag,
  PalmettoTextInput,
} from '@palmetto/dsm-components';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ApprovedVendorAllocation,
  isManufacturerListProgramType,
  ProgramType,
  ProgramTypeLabels,
  UserPermissions,
  VendorEquipmentTypes,
} from 'types';
import type { BulkUpdateApprovedVendorItemsRequest } from 'types';

import { ProgramTypeTags } from '../shared/ProgramTypeTags';

import { ApprovedVendorAllocationLabels, ApprovedVendorAllocationTagColors } from './allocationLabels';
import { ApprovedVendorBulkEditDialog } from './ApprovedVendorBulkEditDialog';
import { VendorEquipmentTypeLabels } from './equipmentTypeLabels';

const pageSize = 25;

const equipmentTypeOptions = Object.values(VendorEquipmentTypes).map((value) => ({
  value,
  label: VendorEquipmentTypeLabels[value],
}));
const programTypeOptions = Object.values(ProgramType)
  .filter((value) => !isManufacturerListProgramType(value))
  .map((value) => ({ value, label: ProgramTypeLabels[value] }));
const allocationOptions = Object.values(ApprovedVendorAllocation).map((value) => ({
  value,
  label: ApprovedVendorAllocationLabels[value],
}));

const equipmentTypeValues: readonly string[] = Object.values(VendorEquipmentTypes);
const programTypeValues: readonly string[] = Object.values(ProgramType);
const allocationValues: readonly string[] = Object.values(ApprovedVendorAllocation);

// Query params are user-editable, so a raw cast would hand a bogus enum value to the API.
const enumParam = <T extends string>(raw: string | null, allowed: readonly string[]): T | '' =>
  raw && allowed.includes(raw) ? (raw as T) : '';

const pageParam = (raw: string | null): number => {
  const parsed = Math.floor(Number(raw));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const clearIcon = <PalmettoIcon name="x" size="xs" />;

export const ApprovedVendorList = () => {
  const navigate = useNavigate();
  const canEditAvl = usePermissions().includes(UserPermissions.canImportAvlData);

  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') ?? '';
  const equipmentType = enumParam<VendorEquipmentTypes>(searchParams.get('equipmentType'), equipmentTypeValues);
  const programType = enumParam<ProgramType>(searchParams.get('programType'), programTypeValues);
  const allocation = enumParam<ApprovedVendorAllocation>(searchParams.get('allocation'), allocationValues);
  const page = pageParam(searchParams.get('page'));
  const sortBy = searchParams.get('sortBy') ?? '';
  const sortDir = searchParams.get('sortDir') === 'desc' ? 'desc' : 'asc';

  const { data, isLoading, isFetching, error, refetch } = useGetAvlItemsQuery({
    ...(search ? { search } : {}),
    ...(equipmentType ? { equipmentType } : {}),
    ...(programType ? { programType } : {}),
    ...(allocation ? { allocation } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
    limit: pageSize,
    offset: page * pageSize,
  });

  const items = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasActiveFilters = Boolean(search || equipmentType || programType || allocation);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = new URLSearchParams(searchParams);
      if (e.target.value) next.set('search', e.target.value);
      else next.delete('search');
      next.delete('page');
      // Replace rather than push: one history entry per keystroke would make Back unusable.
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleEquipmentTypeChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('equipmentType', value);
      else next.delete('equipmentType');
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleProgramTypeChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('programType', value);
      else next.delete('programType');
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleAllocationChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('allocation', value);
      else next.delete('allocation');
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('search');
    next.delete('equipmentType');
    next.delete('programType');
    next.delete('allocation');
    next.delete('page');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(nextPage));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const sortState: DataTableSortState | null = useMemo(
    () => (sortBy ? { columnId: sortBy, direction: sortDir === 'desc' ? 'descending' : 'ascending' } : null),
    [sortBy, sortDir],
  );

  const handleSortChange = useCallback(
    (next: DataTableSortState | null) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('page');

      if (next) {
        nextParams.set('sortBy', next.columnId);
        nextParams.set('sortDir', next.direction === 'descending' ? 'desc' : 'asc');
      } else {
        nextParams.delete('sortBy');
        nextParams.delete('sortDir');
      }

      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const handleNewItem = useCallback(() => navigate('/admin/approved-vendors/new'), [navigate]);
  const handleManufacturers = useCallback(() => navigate('/admin/approved-vendors/manufacturers'), [navigate]);

  const getRowId = useCallback((row: AvlItem) => row.id, []);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const handleSelectionChange = useCallback((ids: string[]) => setSelectedRowIds(ids), []);

  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const handleBulkEdit = useCallback(() => setIsBulkEditOpen(true), []);
  const handleCloseBulkEdit = useCallback(() => {
    setIsBulkEditOpen(false);
    // The underlying list will have changed after a successful bulk edit (refetched via
    // invalidatesTags), so stale selected ids would no longer match what's shown.
    setSelectedRowIds([]);
  }, []);

  const selectedItems = useMemo(
    () => items.filter((item) => selectedRowIds.includes(item.id)),
    [items, selectedRowIds],
  );

  const [bulkUpdateAvlItems] = useBulkUpdateAvlItemsMutation();
  const handleBulkEditSubmit = useCallback(
    (payload: BulkUpdateApprovedVendorItemsRequest) => bulkUpdateAvlItems(payload).unwrap(),
    [bulkUpdateAvlItems],
  );

  const columns: DataTableColumn<AvlItem>[] = useMemo(
    () => [
      {
        id: 'manufacturer',
        header: 'Manufacturer',
        sortable: true,
      },
      {
        id: 'modelNumber',
        header: 'Model',
        width: '180px',
        sortable: true,
        cellType: 'link',
        render: (row) => <Link to={`/admin/approved-vendors/${row.id}`}>{row.modelNumber}</Link>,
      },
      { id: 'name', header: 'Name', width: '220px', sortable: true },
      {
        id: 'sku',
        header: 'SKU',
        width: '160px',
        sortable: true,
        render: (row) => row.sku ?? '--',
      },
      {
        id: 'equipmentType',
        header: 'Equipment Type',
        width: '160px',
        sortable: true,
        render: (row) => VendorEquipmentTypeLabels[row.equipmentType] ?? row.equipmentType,
      },
      {
        id: 'programTypes',
        header: 'Program Types',
        width: '300px',
        cellType: 'tag',
        render: (row) => <ProgramTypeTags programTypes={row.programTypes} />,
      },
      {
        id: 'allocation',
        header: 'Allocation',
        width: '140px',
        sortable: true,
        cellType: 'tag',
        render: (row) =>
          row.allocation ? (
            <PalmettoTag
              label={ApprovedVendorAllocationLabels[row.allocation] ?? row.allocation}
              color={ApprovedVendorAllocationTagColors[row.allocation]}
            />
          ) : (
            '--'
          ),
      },
      {
        id: 'modifiedByName',
        header: 'Updated By',
        width: '200px',
        sortable: true,
        render: (row) => row.modifiedByName ?? row.createdByName ?? '--',
      },
    ],
    [],
  );

  return (
    <DsmPageContainer title="Approved Vendor List">
      <PageHeader title="Approved Vendor List">
        <div className="flex shrink-0 items-center gap-8 whitespace-nowrap">
          <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleManufacturers}>
            Manufacturers
          </PalmettoButton>
          {canEditAvl && (
            <PalmettoButton
              type="button"
              emphasis="secondary"
              layout="hug"
              disabled={selectedRowIds.length < 2}
              onClick={handleBulkEdit}
            >
              {`Bulk Edit (${selectedRowIds.length})`}
            </PalmettoButton>
          )}
          {canEditAvl && (
            <PalmettoButton emphasis="primary" onClick={handleNewItem}>
              + New Item
            </PalmettoButton>
          )}
        </div>
      </PageHeader>

      <div className="mx-6 mb-4 flex flex-row items-baseline gap-16">
        <div className="flex-1">
          <PalmettoTextInput
            id="avl-search"
            label="Search"
            placeholder="Search manufacturer, model, or name"
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <PalmettoButton
          type="button"
          emphasis="text"
          size="small"
          layout="hug"
          className="gap-6"
          disabled={!hasActiveFilters}
          onClick={handleClearFilters}
          trailingIcon={clearIcon}
        >
          Clear all filters
        </PalmettoButton>
      </div>

      <div className="mx-6 mb-4 grid grid-cols-3 gap-4">
        <PalmettoSelect
          deselectable
          label="Equipment Type"
          placeholder="All"
          items={equipmentTypeOptions.map((opt) => ({ ...opt, selected: opt.value === equipmentType }))}
          onValueChange={handleEquipmentTypeChange}
        />
        <PalmettoSelect
          deselectable
          label="Program Type"
          placeholder="All"
          items={programTypeOptions.map((opt) => ({ ...opt, selected: opt.value === programType }))}
          onValueChange={handleProgramTypeChange}
        />
        <PalmettoSelect
          deselectable
          label="Allocation"
          placeholder="All"
          items={allocationOptions.map((opt) => ({ ...opt, selected: opt.value === allocation }))}
          onValueChange={handleAllocationChange}
        />
      </div>

      <div className="mx-6 overflow-x-auto">
        <PalmettoDataTable
          columns={columns}
          data={items}
          getRowId={getRowId}
          sortState={sortState}
          onSortChange={handleSortChange}
          isLoading={isLoading || isFetching}
          error={error}
          onRetry={refetch}
          selectable
          selectedRowIds={selectedRowIds}
          onSelectionChange={handleSelectionChange}
          ariaLabel="Approved vendor list"
        />
      </div>

      {!error && totalPages > 1 && (
        <div className="mx-6 mt-4">
          <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}

      {isBulkEditOpen && (
        <ApprovedVendorBulkEditDialog
          items={selectedItems}
          onClose={handleCloseBulkEdit}
          onSubmit={handleBulkEditSubmit}
        />
      )}
    </DsmPageContainer>
  );
};
